import React from 'react';
import { Calendar, Check, ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';

// Live 17hats scheduling link, already in use across the rest of the site.
const HATS_URL = 'https://audioandco.17hats.com/p#/scheduling/rhrvxsrbdknkddgbdpbhncsvwndbhtdf';

// Snapshot of "from" prices across every service the call can cover.
// Kept in sync with the individual service pages and estimators. Updating a
// price on a service page? Update it here too.
const SERVICES_SNAPSHOT = [
  { label: 'Podcast editing',            price: 'from £90/episode',           to: '/podcast-editing' },
  { label: 'Podcast launch package',     price: 'from £2,400 one-off',        to: '/podcast-launch' },
  { label: 'Audiobook production',       price: 'from £60/hour',              to: '/audiobook-production' },
  { label: 'Self-publishing (Amazon)',   price: 'from £7.50 per 1,000 words', to: '/self-publishing' },
  { label: 'Business website build',     price: 'from £495 one-off',          to: '/authority-hub' },
  { label: 'The Pro Plan (bundle)',      price: '£280/week · £1,213/month',   to: '/pro-plan' },
];

export default function BookCall() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Book a Free 30-Minute Fit Call | Audio & Co."
        description="Book a free 30-minute call about podcast management, audiobooks, book publishing, business websites or the Pro Plan. No pitch, no pressure."
        url="https://audioandco.com/book"
      />

      <Section bg="bg" innerClassName="!py-12 sm:!py-16" testId="book-page">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Step 1</p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl leading-[1.1] tracking-tight mt-3">
            Book your free 30-min fit call.
          </h1>
          <p className="text-lg text-[#475569] mt-5 leading-relaxed">
            A quick call so we can hear what you&rsquo;re building and confirm we&rsquo;re the right team
            to help. Every service is on the table &mdash; podcast editing, podcast launch, audiobook production,
            self-publishing on Amazon, business website builds, or the full Pro Plan bundle.
          </p>
        </div>

        {/* What we'll cover */}
        <div className="mt-10 max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-8 shadow-[0_10px_30px_rgba(30,42,68,0.04)]">
          <h2 className="font-display font-semibold text-[#1E2A44] text-xl">What we&rsquo;ll cover</h2>
          <ul className="mt-4 space-y-3">
            {[
              'What you\u2019re trying to launch, publish or scale.',
              'Which service (or bundle) actually fits your budget, timeline and content rhythm.',
              'A clear next step if we\u2019re a fit \u2014 start date, onboarding and an exact quote.',
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[#475569]">
                <Check className="w-5 h-5 text-[#BC516F] shrink-0 mt-0.5" /> {b}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-[#64748B]">
            30 minutes, on a Zoom call, with Jo. No pitch deck, no pressure &mdash; if we&rsquo;re not the right
            fit we&rsquo;ll say so on the call.
          </p>
        </div>

        {/* Services snapshot — one glance at every entry-level price */}
        <div className="mt-6 max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-8 shadow-[0_10px_30px_rgba(30,42,68,0.04)]" data-testid="book-services-snapshot">
          <h2 className="font-display font-semibold text-[#1E2A44] text-xl">Services on the table</h2>
          <p className="text-sm text-[#64748B] mt-1">Entry-level pricing so you know what ballpark you&rsquo;re walking into. Every one is scoped exactly on the call.</p>
          <ul className="mt-5 divide-y divide-[#E2E8F0]">
            {SERVICES_SNAPSHOT.map((s) => (
              <li key={s.to} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <a
                  href={s.to}
                  className="text-[#1E2A44] font-semibold text-sm hover:text-[#BC516F]"
                  data-testid={`book-service-${s.to.replace(/\//g, '')}`}
                >
                  {s.label}
                </a>
                <span className="text-sm text-[#475569]">{s.price}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 17hats scheduling iframe */}
        <div className="mt-10 max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_10px_30px_rgba(30,42,68,0.06)]">
          <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center gap-2 text-sm text-[#64748B]">
            <Calendar className="w-4 h-4 text-[#BC516F]" />
            <span>Pick a time below, powered by 17hats</span>
          </div>
          <iframe
            src={HATS_URL}
            title="Book a free 30-min fit call with Jo Day"
            className="w-full bg-white"
            style={{ height: '900px', border: 0 }}
            loading="lazy"
            data-testid="book-iframe"
          />
        </div>

        {/* Fallback for users on browsers that block iframes */}
        <p className="mt-6 text-center text-sm text-[#64748B]" data-testid="book-fallback">
          Calendar not loading?{' '}
          <a
            href={HATS_URL}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold"
            data-testid="book-fallback-link"
          >
            Open scheduling in a new tab <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </p>
      </Section>
    </main>
  );
}
